/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { KeyboardEvent, useEffect, useState } from 'react';
import { styled, MenuItem, useMediaQuery, useTheme, IconButton } from '@mui/material';
import { Box } from '@mui/system';
import { SearchResponse } from 'typesense/lib/Typesense/Documents';
import { createSearchParams, useNavigate } from 'react-router-dom';

import { ComponentFilters, ProductFilterProps } from './types';
import Autocomplete from '../../../../components/Autocomplete';
import { ImageWithPlaceholder } from '../../../../components/ImageWithPlaceholder';
import PlaceholderImage from '../../../../assets/images/image-placeholder.png';
import { DateRangeType } from '../../../../components/DateRangePicker';
import { TestIds } from '../../../../testIds';
import Analytics from '../../../../services/analytics';
import { FiltersCard } from '../../../../components/Filters/FilterCard';
import ChipMenu from '../../../../components/ChipMenu/ChipMenu';
import { formatAgreementDates } from '../../../sales-agreements/utils/formatAgreementDates';
import { Button } from '../../../../components/Button';
import { CATEGORY_DEFAULT_OPTION } from '../../../../util/consts';
import MaterialSymbol from '../../../../components/MaterialSymbol';
import { FilterDropdown } from './ProductFilterDropdownLayout';
import {
  DesktopCategoryMenu,
  DesktopDateRangePicker,
  DesktopInStockFilter,
  DesktopPriceRangeFilter,
} from './DesktopFilters';
import { MobileFilterMenu } from './MobileFilters';
import { autoSearch } from '../../../typesense/api';
import { TypesenseProduct } from '../../../typesense/types';
import { generateImageUrl } from '../../../../util/helpers';

const AutocompleteImagePreview = styled(ImageWithPlaceholder)`
  width: 48px;
  height: 48px;
  margin: 4px;
`;

const AutocompleteTextPreview = styled('div')({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whitespace: 'nowrap',
  marginLeft: '4px',
});

const StyledMenuItem = styled(MenuItem)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  height: '64px',
});

interface AutocompleteDataType {
  id: string | number;
  name: string;
  itemId: string;
}

type ChangedValueType = string | AutocompleteDataType | null;

const ProductFilter = ({ filters, selectedFilters, items, components }: ProductFilterProps) => {
  const [value, setValue] = useState<AutocompleteDataType | null>(null);

  useEffect(() => {
    if (selectedFilters.searchText?.value === '') {
      setValue(null);
    }
  }, [selectedFilters.searchText?.value]);

  const isTypesenseSearch = React.useMemo(
    () => components.includes(ComponentFilters.SEARCH_TYPESENSE),
    [components],
  );

  const handleChangeOnSearch = (
    event: React.SyntheticEvent<Element, Event>,
    value: ChangedValueType,
  ) => {
    setFocused(false);
    if (value !== null) {
      const temp = value as AutocompleteDataType;

      if (temp && typeof temp === 'object' && temp.name) {
        Analytics.push({
          dataLayer: {
            event: 'search_product',
            searchTerm: temp?.name,
          },
        });
        selectedFilters.searchText?.setValue(temp.name);
        selectedFilters.searchInput?.setValue(temp.name);
        setValue({
          id: temp.id,
          name: temp.name,
          itemId: temp.itemId,
        });
      } else {
        if (value) {
          Analytics.push({
            dataLayer: {
              event: 'search_term',
              searchTerm: value as string,
            },
          });
        }
        selectedFilters.searchInput?.setValue((value ?? '') as string);
        selectedFilters.searchText?.setValue((value ?? '') as string);
      }
    }
  };

  const handleInputChange = (value: string) => {
    selectedFilters.searchInput?.setValue(value ?? '');
    if (value === '') {
      selectedFilters.searchText?.setValue(value);
      setValue(null);
    }
  };

  const handleDateRange = (dateRange: DateRangeType) => {
    Analytics.push({
      dataLayer: {
        event: 'date_range',
        date_range_from: dateRange.from,
        date_range_to: dateRange.to,
      },
    });

    selectedFilters.dateRange?.setValue(dateRange);
  };

  const submitAndCloseMenu = (callback: React.Dispatch<React.SetStateAction<boolean>>) => {
    // Seperate submit if neccessary : submitChanges()
    callback(false);
  };

  const [timeFilterOpen, setTimeFilterOpen] = React.useState(false);
  const [priceRangeOpen, setPriceRangeOpen] = React.useState(false);
  const [categoryOpen, setCategoryOpen] = React.useState(false);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down('md'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  useEffect(() => {
    setCategoryOpen(false);
    setPriceRangeOpen(false);
  }, [isMd]);

  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const appliedFilterCount = React.useMemo(() => {
    if (isMd) {
      let count = 0;
      count +=
        selectedFilters.category.value !== '' &&
        selectedFilters.category.value !== CATEGORY_DEFAULT_OPTION
          ? 1
          : 0;
      count += selectedFilters.priceRange.value.from || selectedFilters.priceRange.value.to ? 1 : 0;
      count +=
        !isTypesenseSearch &&
        (selectedFilters.dateRange.value.from || selectedFilters.dateRange.value.to)
          ? 1
          : 0;
      return count;
    }
    return 0;
  }, [isMd, isTypesenseSearch, selectedFilters]);

  const mobileFilters = React.useMemo(() => {
    return (
      <>
        <Button
          sx={{ width: '100%' }}
          startIcon={<MaterialSymbol>page_info</MaterialSymbol>}
          variant='outlined'
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          Filter products {appliedFilterCount > 0 && `(${appliedFilterCount})`}
        </Button>
        <MobileFilterMenu
          components={components}
          title='Filter products'
          backAction='Refine filters'
          selectedFilters={selectedFilters}
          filters={filters}
          items={items}
          open={mobileMenuOpen}
          handleClose={() => setMobileMenuOpen(false)}
        />
      </>
    );
  }, [selectedFilters, mobileMenuOpen, appliedFilterCount]);

  const getFilterPriceValue = React.useMemo(() => {
    if (!selectedFilters.priceRange?.value.from && !selectedFilters.priceRange?.value.to) {
      return 'Any';
    }
    const from = selectedFilters.priceRange?.value.from
      ? '$' + selectedFilters.priceRange.value.from
      : 'Any';
    const to = selectedFilters.priceRange?.value.to
      ? '$' + selectedFilters.priceRange.value.to
      : 'Any';
    return `${from} - ${to}`;
  }, [selectedFilters.priceRange]);

  const getFilterLabel = () => {
    const split = selectedFilters.category?.value?.split('>') || [];
    if (split.length <= 0) return '';
    if (!split[1]) return split[0];
    return `${split[0]} > ${split[1]}`;
  };

  const getOptionLabel = (option: string | AutocompleteDataType) => {
    const temp = option as AutocompleteDataType;
    if (temp && typeof temp === 'object' && temp.name) {
      return temp.name;
    }
    return JSON.stringify(option);
  };

  const filterComponents = [
    {
      isChipMenu: false,
      type: ComponentFilters.IN_STOCK,
      label: 'In Stock Only',
      open: false,
      setOpen: () => null,
      testId: TestIds.components.filters.inStockCheckbox,
      value: selectedFilters.inStock.value.toString(),
      onClear: () => selectedFilters.inStock.setValue(false),
      onSubmit: () => null,
      content: <DesktopInStockFilter selectedFilters={selectedFilters} />,
    },
    {
      isChipMenu: true,
      type: ComponentFilters.CATEGORY,
      label: 'Category',
      testId: TestIds.components.filters.categoryChip,
      open: categoryOpen,
      setOpen: setCategoryOpen,
      value: getFilterLabel(),
      onClear: () => selectedFilters.category?.setValue(CATEGORY_DEFAULT_OPTION),
      onSubmit: () => submitAndCloseMenu(setCategoryOpen),
      content: selectedFilters.category ? (
        <DesktopCategoryMenu
          filters={filters}
          selectedFilters={{
            category: selectedFilters.category,
          }}
          showSubCategories={isTypesenseSearch}
        />
      ) : null,
    },
    {
      isChipMenu: true,
      type: ComponentFilters.DATE,
      label: 'Time period',
      open: timeFilterOpen,
      setOpen: setTimeFilterOpen,
      testId: TestIds.components.filters.dateRangeChip,
      value:
        selectedFilters.dateRange?.value.from && selectedFilters.dateRange.value.to
          ? formatAgreementDates(
              selectedFilters.dateRange.value.from,
              selectedFilters.dateRange.value.to,
            )
          : 'Any',
      onClear: () =>
        selectedFilters.dateRange?.setValue({
          to: undefined,
          from: undefined,
        }),
      onSubmit: () => submitAndCloseMenu(setTimeFilterOpen),
      content: (
        <DesktopDateRangePicker
          dateRange={selectedFilters.dateRange?.value}
          handleDateRange={handleDateRange}
        />
      ),
    },
    {
      isChipMenu: true,
      type: ComponentFilters.PRICE,
      label: 'Price range',
      testId: TestIds.components.filters.priceRangeChip,
      open: priceRangeOpen,
      setOpen: setPriceRangeOpen,
      value: getFilterPriceValue,
      onClear: () =>
        selectedFilters.priceRange?.setValue({
          to: undefined,
          from: undefined,
        }),
      onSubmit: () => submitAndCloseMenu(setPriceRangeOpen),
      content: selectedFilters.priceRange ? (
        <DesktopPriceRangeFilter
          selectedFilters={{
            priceRange: selectedFilters.priceRange,
          }}
        />
      ) : null,
    },
  ];

  const desktopFilters = React.useMemo(() => {
    const chipComponents = filterComponents.filter(
      (e) => components.includes(e.type) && e.isChipMenu,
    );
    const nonChipComponents = filterComponents.filter(
      (e) => components.includes(e.type) && !e.isChipMenu,
    );
    return (
      <Box display='flex' gap={2} flexWrap='wrap'>
        {chipComponents.map((e) => {
          return (
            <ChipMenu
              key={e.label}
              {...e}
              dropdown={
                <FilterDropdown title={e.label} onClear={e.onClear} onSubmit={e.onSubmit}>
                  {e.content}
                </FilterDropdown>
              }
            />
          );
        })}

        {nonChipComponents.map((e) => {
          return (
            <Box key={e.label} sx={{ height: '42px', borderRadius: '32px', padding: '3px' }}>
              {e.content}
            </Box>
          );
        })}
      </Box>
    );
  }, [filterComponents]);

  const [focused, setFocused] = useState(false);

  const [typesenseHits, setTypesenseHits] = useState<SearchResponse<TypesenseProduct> | null>(null);
  useEffect(() => {
    if (isTypesenseSearch) {
      autoSearch(selectedFilters.searchInput.value).then((hits) => {
        setTypesenseHits(hits);
      });
    }
  }, [selectedFilters.searchInput.value, selectedFilters.searchText.value, isTypesenseSearch]);

  const handleSearchIconClick = React.useCallback(() => {
    setFocused(false);
    selectedFilters.searchText.setValue(selectedFilters.searchInput.value);
    if (selectedFilters.searchInput.value) {
      Analytics.push({
        dataLayer: {
          event: 'search_term',
          searchTerm: selectedFilters.searchInput.value,
        },
      });
    }
  }, [selectedFilters.searchInput.value]);

  const onEnterKeyPress = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key.toLowerCase() === 'enter') {
      (event.target as HTMLInputElement).blur(); // force keyboard to close
      handleSearchIconClick();
    }
  };

  const searchBar = (): JSX.Element | undefined => {
    const sx = { width: isLg ? '512px' : 'auto' };

    if (components.includes(ComponentFilters.SEARCH_PAST)) {
      return (
        <Autocomplete
          sx={sx}
          value={value}
          freeSolo
          open={focused}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          testId={TestIds.components.filters.searchProductField}
          placeholder='Search Farmlands products'
          options={items
            .filter((a) =>
              `${a.productId}${a.description}`.includes(selectedFilters.searchInput?.value),
            )
            .slice(0, 10)
            .map((product) => ({
              id: product.productId.toString(),
              name: product.name,
              itemId: product.itemNumber,
            }))}
          onInputChange={(_, value) => {
            setFocused(true);
            handleInputChange(value);
          }}
          onKeyUp={onEnterKeyPress}
          endIcon={
            <IconButton
              onClick={handleSearchIconClick}
              color='primary'
              title='Search'
              id={TestIds.components.filters.searchProductIconBtn}
            >
              <MaterialSymbol>search</MaterialSymbol>
            </IconButton>
          }
          inputValue={selectedFilters.searchInput?.value}
          getOptionLabel={(option) => getOptionLabel(option)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(event, newValue: string | AutocompleteDataType | null) =>
            handleChangeOnSearch(event, newValue)
          }
          renderOption={(props, option) => {
            return (
              <StyledMenuItem
                {...props}
                selected={option.name === selectedFilters.searchText?.value}
                key={option.id}
                data-testid={TestIds.components.filters.searchProductFieldMenuItem}
                id={`${option.id}`}
              >
                <AutocompleteImagePreview
                  src={generateImageUrl(option.itemId)}
                  size={48 * 2}
                  placeholderImageUrl={PlaceholderImage}
                  alt=''
                />
                <AutocompleteTextPreview>{option.name}</AutocompleteTextPreview>
              </StyledMenuItem>
            );
          }}
        />
      );
    } else if (components.includes(ComponentFilters.SEARCH_TYPESENSE)) {
      return (
        <Autocomplete
          sx={sx}
          freeSolo
          value={value}
          open={focused}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          testId={TestIds.components.filters.searchProductField}
          placeholder='Search Farmlands products'
          options={
            typesenseHits?.hits?.map(
              (e) =>
                ({
                  id: e.document.id,
                  name: e.document.name,
                  itemId: e.document.item_id,
                }) as AutocompleteDataType,
            ) ?? []
          }
          onInputChange={(_, value) => {
            setFocused(true);
            handleInputChange(value);
          }}
          onKeyUp={onEnterKeyPress}
          endIcon={
            <IconButton
              onClick={handleSearchIconClick}
              color='primary'
              title='Search'
              id={TestIds.components.filters.searchProductIconBtn}
            >
              <MaterialSymbol>search</MaterialSymbol>
            </IconButton>
          }
          inputValue={selectedFilters.searchInput.value}
          getOptionLabel={(option) => getOptionLabel(option)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(event, newValue: string | AutocompleteDataType | null) =>
            handleChangeOnSearch(event, newValue)
          }
          renderOption={(props, option) => {
            return (
              <StyledMenuItem
                {...props}
                selected={option.name === selectedFilters.searchText?.value}
                key={option.name}
                data-testid={TestIds.components.filters.searchProductFieldMenuItem}
                id={`${option.id}`}
                onClick={() => {
                  // Navigate ProductDetails
                  const params = createSearchParams({
                    size: '',
                    colour: '',
                  });
                  navigate(
                    {
                      pathname: `/products/${option.id}`,
                      search: params.toString(),
                    },
                    {
                      state: {
                        category: selectedFilters.category?.value?.split('>')[0],
                        subCategory: selectedFilters.category?.value?.split('>')[1],
                        productTab: 1,
                      },
                    },
                  );
                }}
              >
                <AutocompleteImagePreview
                  size={48 * 2}
                  src={generateImageUrl(option.itemId)}
                  placeholderImageUrl={PlaceholderImage}
                  alt=''
                />
                <AutocompleteTextPreview>{option.name}</AutocompleteTextPreview>
              </StyledMenuItem>
            );
          }}
        />
      );
    }
  };

  return (
    <FiltersCard>
      <Box width='100%'>{searchBar()}</Box>
      <Box width='100%'>{isMd ? mobileFilters : desktopFilters}</Box>
    </FiltersCard>
  );
};

export default ProductFilter;
